import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Alert,
  TablePagination,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import UndoIcon from '@mui/icons-material/Undo';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import axiosInstance from '../../../../utils/axiosInstance';
import {
  OFFER_STATUSES, OFFER_STATUS_COLORS,
} from '../../../../constants/hrm';
import {
  useOffers, useCreateOffer, useUpdateOffer,
  useApplicants, useJobs,
} from '../../../../hooks/useHRM';
import { hasPermission } from '../../../../utils/permissionUtils';

const initialFormData = {
  applicant_id: '',
  job_id: '',
  salary_offered: '',
  bonus: '',
  benefits: '',
  start_date: '',
  expiry_date: '',
};

const OfferList = () => {
  const { chatbotId } = useParams();

  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingOffer, setDeletingOffer] = useState(null);
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const [statusAction, setStatusAction] = useState({ offer: null, newStatus: '' });
  const [error, setError] = useState(null);

  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  useEffect(() => {
    const checkPermissions = async () => {
      const view = await hasPermission('hrm', 'view');
      const modify = await hasPermission('hrm', 'add|edit');
      setCanView(view);
      setCanModify(modify);
    };
    checkPermissions();
  }, []);

  const filters = {
    page: page + 1,
    per_page: rowsPerPage,
    ...(statusFilter && { status: statusFilter }),
  };

  const { data: offersData, isLoading: loading, refetch: refetchOffers } = useOffers(chatbotId, filters, { enabled: !!chatbotId && canView });
  const { data: applicantsData } = useApplicants(chatbotId, { per_page: 100 }, { enabled: !!chatbotId && canView });
  const { data: jobsData } = useJobs(chatbotId, { per_page: 100 }, { enabled: !!chatbotId && canView });

  const offers = offersData?.data || [];
  const totalCount = offersData?.pagination?.total || 0;
  const applicants = applicantsData?.data || [];
  const jobs = jobsData?.data || [];

  const createOffer = useCreateOffer(chatbotId);
  const updateOffer = useUpdateOffer(chatbotId);

  const saving = createOffer.isPending;

  const handleOpenDialog = () => {
    setFormData(initialFormData);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData(initialFormData);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        applicant_id: Number(formData.applicant_id),
        job_id: formData.job_id ? Number(formData.job_id) : null,
        salary_offered: formData.salary_offered ? Number(formData.salary_offered) : null,
        bonus: formData.bonus ? Number(formData.bonus) : null,
      };
      await createOffer.mutateAsync(payload);
      handleCloseDialog();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create offer');
    }
  };

  const handleStatusUpdate = async (offer, newStatus) => {
    try {
      await updateOffer.mutateAsync({
        id: offer.id,
        status: newStatus,
      });
      setStatusConfirmOpen(false);
      setStatusAction({ offer: null, newStatus: '' });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update offer status');
    }
  };

  const confirmStatusUpdate = (offer, newStatus) => {
    setStatusAction({ offer, newStatus });
    setStatusConfirmOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/api/hrm/offers/${deletingOffer.id}`, {
        params: { chatbot_id: chatbotId },
      });
      setDeleteConfirmOpen(false);
      setDeletingOffer(null);
      refetchOffers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete offer');
    }
  };

  const getStatusActionLabel = (status) => {
    switch (status) {
      case 'SENT': return 'Send';
      case 'ACCEPTED': return 'Accept';
      case 'REJECTED': return 'Reject';
      case 'WITHDRAWN': return 'Withdraw';
      default: return status;
    }
  };

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">You do not have permission to view the HRM module.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Offers
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetchOffers()} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {canModify && (
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleOpenDialog}>
              Create Offer
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="">All</MenuItem>
            {OFFER_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Applicant Name</TableCell>
                  <TableCell>Job Title</TableCell>
                  <TableCell align="right">Salary Offered</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>Expiry Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {offers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No offers found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  offers.map((offer) => (
                    <TableRow key={offer.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {offer.applicant_name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{offer.job_title || '-'}</TableCell>
                      <TableCell align="right">
                        {offer.salary_offered
                          ? offer.salary_offered.toLocaleString()
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {offer.start_date
                          ? new Date(offer.start_date).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {offer.expiry_date
                          ? new Date(offer.expiry_date).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={offer.status}
                          size="small"
                          color={OFFER_STATUS_COLORS[offer.status] || 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {canModify && (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap' }}>
                            {offer.status === 'DRAFT' && (
                              <Tooltip title="Send Offer">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => confirmStatusUpdate(offer, 'SENT')}
                                >
                                  <SendIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {offer.status === 'SENT' && (
                              <>
                                <Tooltip title="Mark as Accepted">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => confirmStatusUpdate(offer, 'ACCEPTED')}
                                  >
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Mark as Rejected">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => confirmStatusUpdate(offer, 'REJECTED')}
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            {(offer.status === 'DRAFT' || offer.status === 'SENT') && (
                              <Tooltip title="Withdraw Offer">
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => confirmStatusUpdate(offer, 'WITHDRAWN')}
                                >
                                  <UndoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {offer.status === 'DRAFT' && (
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    setDeletingOffer(offer);
                                    setDeleteConfirmOpen(true);
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        )}
      </TableContainer>

      {/* Create Offer Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Create Offer</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Applicant</InputLabel>
                <Select
                  value={formData.applicant_id}
                  label="Applicant"
                  onChange={(e) => handleFormChange('applicant_id', e.target.value)}
                >
                  {applicants.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.first_name} {a.last_name} ({a.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Job</InputLabel>
                <Select
                  value={formData.job_id}
                  label="Job"
                  onChange={(e) => handleFormChange('job_id', e.target.value)}
                >
                  <MenuItem value="">None</MenuItem>
                  {jobs.map((j) => (
                    <MenuItem key={j.id} value={j.id}>{j.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Salary Offered"
                type="number"
                value={formData.salary_offered}
                onChange={(e) => handleFormChange('salary_offered', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Bonus"
                type="number"
                value={formData.bonus}
                onChange={(e) => handleFormChange('bonus', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Benefits"
                value={formData.benefits}
                onChange={(e) => handleFormChange('benefits', e.target.value)}
                multiline
                rows={3}
                placeholder="e.g., Health insurance, 401k match, PTO, etc."
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.start_date}
                onChange={(e) => handleFormChange('start_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Expiry Date"
                type="date"
                value={formData.expiry_date}
                onChange={(e) => handleFormChange('expiry_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formData.applicant_id || !formData.salary_offered}
          >
            {saving ? <CircularProgress size={24} /> : 'Create Offer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Confirmation Dialog */}
      <Dialog open={statusConfirmOpen} onClose={() => setStatusConfirmOpen(false)}>
        <DialogTitle>Confirm Status Update</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {getStatusActionLabel(statusAction.newStatus).toLowerCase()} this offer
            {statusAction.offer?.applicant_name ? ` for ${statusAction.offer.applicant_name}` : ''}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={
              statusAction.newStatus === 'ACCEPTED'
                ? 'success'
                : statusAction.newStatus === 'REJECTED' || statusAction.newStatus === 'WITHDRAWN'
                ? 'error'
                : 'primary'
            }
            onClick={() => handleStatusUpdate(statusAction.offer, statusAction.newStatus)}
          >
            {getStatusActionLabel(statusAction.newStatus)}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Offer</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this offer? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OfferList;
